import { useState, useMemo } from 'react';

export const useOrderingFilters = (initialData = []) => {
  const [search, _setSearch] = useState("");
  const [filters, _setFilters] = useState({});
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);

  const setSearch = (val) => {
    _setSearch(val);
    setCurrentPage(1);
  };

  const setFilters = (key, value) => {
    if (typeof key === 'function') {
      _setFilters(key);
    } else if (typeof key === 'object' && key !== null) {
      _setFilters(prev => ({ ...prev, ...key }));
    } else {
      _setFilters(prev => ({ ...prev, [key]: value }));
    }
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!initialData || !Array.isArray(initialData)) return [];

    let result = [...initialData];

    // Search Filter
    if (search && search.trim() !== '') {
      const q = search.toLowerCase().trim();
      result = result.filter(item =>
        (item.projectName && item.projectName.toLowerCase().includes(q)) ||
        (item.quotationNo && item.quotationNo.toLowerCase().includes(q)) ||
        (item.site && item.site.toLowerCase().includes(q)) ||
        (item.referenceDesigner && item.referenceDesigner.toLowerCase().includes(q)) ||
        (item.status && item.status.toLowerCase().includes(q))
      );
    }

    // Status Filter
    if (filters.status && filters.status.toLowerCase() !== 'all' && filters.status !== 'All Statuses') {
      result = result.filter(item => item.status === filters.status);
    }

    // Category Filter
    if (filters.category && filters.category.toLowerCase() !== 'all' && filters.category !== 'All Categories' && filters.category !== 'All Types') {
      result = result.filter(item => (item.category || 'Appliances').toLowerCase() === filters.category.toLowerCase());
    }

    // Date Range Filter
    if (filters.fromDate || filters.toDate || (filters.date && filters.date !== 'all')) {
      result = result.filter(item => {
        if (!item.date && !item.createdAt) return false;

        const parseDateString = (dateStr) => {
          if (!dateStr) return 0;
          const cleanStr = typeof dateStr === 'string' ? dateStr.trim() : String(dateStr);
          // Handle DD-MM-YYYY or DD/MM/YYYY
          const parts = cleanStr.split(/[-/]/);
          if (parts.length === 3 && parts[2].length === 4) {
            return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0])).getTime();
          }
          const dt = new Date(cleanStr);
          return isNaN(dt.getTime()) ? 0 : dt.getTime();
        };

        const itemTime = parseDateString(item.date || item.createdAt);
        if (isNaN(itemTime)) return true;
        const itemDateObj = new Date(itemTime);

        if (filters.fromDate || filters.toDate) {
          if (filters.fromDate) {
            const startTime = new Date(filters.fromDate).setHours(0, 0, 0, 0);
            if (itemTime < startTime) return false;
          }
          if (filters.toDate) {
            const endTime = new Date(filters.toDate).setHours(23, 59, 59, 999);
            if (itemTime > endTime) return false;
          }
        } else if (filters.date && filters.date !== "all") {
          const today = new Date();
          if (filters.date === "today") {
            if (itemDateObj.toDateString() !== today.toDateString()) return false;
          } else if (filters.date === "this_week") {
            const first = today.getDate() - today.getDay();
            const firstDay = new Date(today.setDate(first));
            firstDay.setHours(0, 0, 0, 0);
            if (itemTime < firstDay.getTime()) return false;
          } else if (filters.date === "this_month") {
            if (itemDateObj.getMonth() !== today.getMonth() || itemDateObj.getFullYear() !== today.getFullYear()) return false;
          } else if (filters.date === "this_year") {
            if (itemDateObj.getFullYear() !== today.getFullYear()) return false;
          }
        }
        return true;
      });
    }

    // Sorting
    result.sort((a, b) => {
      const getTime = (item) => {
        const dateStr = item.date || item.createdAt;
        if (!dateStr) return 0;
        const parts = dateStr.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
          return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
        }
        return new Date(dateStr).getTime();
      };

      if (sortBy === 'newest') {
        return getTime(b) - getTime(a);
      } else if (sortBy === 'oldest') {
        return getTime(a) - getTime(b);
      }
      return 0;
    });

    return result;
  }, [initialData, search, filters, sortBy]);

  // Pagination Logic
  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const showPagination = filteredData.length > 10;

  // Reset to page 1 if data changes
  useMemo(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredData.length, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  return {
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    filteredData,
    paginatedData,
    currentPage,
    setCurrentPage,
    totalPages,
    showPagination
  };
};
